if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.initializeForm();
      }

      initializeForm() {
        try {
          this.form = this.querySelector('form');
          if (!this.form) {
            console.error('Form not found');
            return;
          }

          const idInput = this.form.querySelector('[name=id]');
          if (idInput) idInput.disabled = false;

          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          this.submitButton = this.querySelector('[type="submit"]');
          this.generationSelector = document.querySelector('.generation-selector');

          if (this.submitButton && document.querySelector('cart-drawer')) {
            this.submitButton.setAttribute('aria-haspopup', 'dialog');
          }

          this.hideErrors = this.dataset.hideErrors === 'true';
          this.boundCartKey = null;
          this.savedBuildFingerprint = null;
          this.boundSnapshotReady = false;
          this.userChangedBuild = false;
          this.hydratingBuild = true;
          this.productId = this.dataset.productId ? Number(this.dataset.productId) : null;
          this.cartBuildKeys = this.parseCartBuildKeys();
          this.secondaryActions = this.querySelector('[data-configurator-secondary-actions]');
          this.startOverButton = this.querySelector('[data-configurator-start-over]');
          this.submitLabel = this.submitButton?.querySelector('span');

          // Install location dropdown
          this.installLocationRequired = this.dataset.installLocationRequired === 'true';
          this.selectedInstallLocation = this.installLocationSelector ? this.installLocationSelector.value : '';

          this.syncSelectedStateFromDom();
          this.buildRoot = this.closest('product-info') || this;
          this.bindBuildInteractionListeners();
          this.buildRoot.addEventListener('change', this.onBuildInputChange.bind(this));

          document.addEventListener('vehicleSelected', this.onVehicleSelected.bind(this));
          document.addEventListener('generationSelected', this.onGenerationSelected.bind(this));
          document.addEventListener('installLocationSelected', this.onInstallLocationSelected.bind(this));
          document.addEventListener('configurator:ready', this.onConfiguratorReady.bind(this));

          if (this.startOverButton) {
            this.startOverButton.addEventListener('click', this.onStartOverClick.bind(this));
          }

          this.skipCartBinding = this.consumeStartOverUnbind();
          this.boundCartKey = this.skipCartBinding ? null : this.resolveBoundCartKey();
          if (this.skipCartBinding) this.cartBuildKeys = [];
          this.updateCartSubmitUI();
          this.initCartLineBinding();
          this.validateForm();
          this.maybeCaptureBoundSnapshot();
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdated.bind(this));

          // global.js resets submit label to "Add to cart" after variant HTML refresh
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            if (!this.hydratingBuild) this.markBuildChanged();
            this.updateSubmitButton(this.getFormValidation().isValid);
            this.updateCartSubmitUI();
          });
        } catch (error) {
          console.error('Error in ProductForm initialization:', error);
        }
      }

      // Looked up live so a re-rendered or late-upgraded dropdown is never missed
      get installLocationSelector() {
        return document.querySelector('install-location-selector');
      }

      isInstallLocationRequired() {
        return this.installLocationRequired || !!this.installLocationSelector;
      }

      getInstallLocationValue() {
        return this.installLocationSelector?.value || this.selectedInstallLocation || '';
      }

      isGenerationRequired() {
        return (
          !!this.generationSelector &&
          !this.generationSelector.hidden &&
          this.generationSelector.style.display !== 'none'
        );
      }

      getFormValidation() {
        const installLocationMissing = this.isInstallLocationRequired() && !this.getInstallLocationValue();
        const fieldErrors = {
          vehicle: false,
          generation: false,
          installLocation: false,
        };
        let isValid = true;
        let errorMessage = '';

        if (!this.selectedVehicle) {
          isValid = false;
          fieldErrors.vehicle = true;
          errorMessage += 'Please select a vehicle. ';
        } else if (this.isGenerationRequired() && !this.selectedGeneration) {
          isValid = false;
          fieldErrors.generation = true;
          errorMessage += 'Please select a generation. ';
        } else if (installLocationMissing) {
          isValid = false;
          fieldErrors.installLocation = true;
          errorMessage += 'Please select an install location. ';
        }

        return { isValid, errorMessage: errorMessage.trim(), fieldErrors };
      }

      updateFieldErrors(fieldErrors, showErrors) {
        if (this.installLocationSelector) {
          const locationHasError = showErrors && fieldErrors.installLocation;
          if (typeof this.installLocationSelector.setErrorState === 'function') {
            this.installLocationSelector.setErrorState(
              locationHasError,
              'Please select an install location.'
            );
          } else {
            const locationButton = this.installLocationSelector.querySelector('.disclosure__button');
            if (locationButton) {
              locationButton.classList.toggle('error', locationHasError);
              locationButton.setAttribute('aria-invalid', locationHasError ? 'true' : 'false');
            }
          }
        }

        const vehicleButton = document.querySelector('#vehicle-model-trigger');
        if (vehicleButton) {
          const vehicleHasError = showErrors && fieldErrors.vehicle;
          vehicleButton.classList.toggle('error', vehicleHasError);
          vehicleButton.setAttribute('aria-invalid', vehicleHasError ? 'true' : 'false');
        }

        const generationSelector = document.querySelector('.generation-selector');
        if (generationSelector) {
          const generationHasError = showErrors && fieldErrors.generation;
          generationSelector.classList.toggle('error', generationHasError);
          generationSelector.setAttribute('aria-invalid', generationHasError ? 'true' : 'false');
        }
      }

      showValidationErrors(validation = this.getFormValidation()) {
        this.updateFieldErrors(validation.fieldErrors, true);
        this.handleErrorMessage(validation.errorMessage || 'Please fill in all required fields.');
      }

      clearValidationErrors() {
        this.updateFieldErrors(
          { vehicle: false, generation: false, installLocation: false },
          false
        );
        this.handleErrorMessage(false);
      }

      syncSelectedStateFromDom() {
        const selected = document.querySelector('#vehicle-model-list .disclosure__link[aria-selected="true"]');
        if (selected) {
          this.selectedVehicle = {
            value: selected.dataset.value || '',
            vehicleSize: selected.dataset.vehicleSize,
            label: (selected.dataset.optionText || selected.textContent || '').trim(),
          };
        }
        if (this.installLocationSelector) {
          this.selectedInstallLocation = this.installLocationSelector.value || this.selectedInstallLocation || '';
        }
      }

      getBuildFingerprint() {
        const root = this.closest('product-info') || this;
        const variantId = this.form?.querySelector('[name=id]')?.value || '';
        const vehicle = this.selectedVehicle?.value || '';
        const generation = this.selectedGeneration || '';
        const install = this.selectedInstallLocation || '';
        const checked = Array.from(root.querySelectorAll('input:checked'))
          .filter((el) => el.name && el.name !== 'id')
          .map((el) => `${el.name}=${el.value}`)
          .sort();
        return JSON.stringify({ variantId, vehicle, generation, install, checked });
      }

      hasBuildChanges() {
        if (!this.boundCartKey) return true;
        if (!this.userChangedBuild) return false;
        if (!this.savedBuildFingerprint) return true;
        return this.getBuildFingerprint() !== this.savedBuildFingerprint;
      }

      maybeCaptureBoundSnapshot() {
        if (!this.boundCartKey) return;
        this.syncSelectedStateFromDom();
        this.savedBuildFingerprint = this.getBuildFingerprint();
        this.boundSnapshotReady = true;
        if (this.hydratingBuild) this.userChangedBuild = false;
        this.updateSubmitButton(this.getFormValidation().isValid);
        this.updateCartSubmitUI();
      }

      markBuildChanged() {
        if (!this.boundCartKey || this.hydratingBuild) return;
        if (this.savedBuildFingerprint && this.getBuildFingerprint() === this.savedBuildFingerprint) return;
        this.userChangedBuild = true;
      }

      bindBuildInteractionListeners() {
        const root = this.buildRoot || this.closest('product-info') || this;
        this.buildRoot = root;
        if (this.onBuildPointerDownHandler) {
          root.removeEventListener('pointerdown', this.onBuildPointerDownHandler);
        }
        this.onBuildPointerDownHandler = this.onBuildPointerDown.bind(this);
        root.addEventListener('pointerdown', this.onBuildPointerDownHandler, { once: true });
      }

      onBuildPointerDown() {
        this.hydratingBuild = false;
      }

      onBuildInputChange() {
        if (this.hydratingBuild) {
          this.maybeCaptureBoundSnapshot();
        } else {
          this.markBuildChanged();
        }
        this.updateSubmitButton(this.getFormValidation().isValid);
        this.updateCartSubmitUI();
      }

      onVehicleSelected(event) {
        try {
          const { value, vehicleSize, label } = event.detail;
          this.selectedVehicle = value ? { value, vehicleSize, label } : null;
          if (this.hydratingBuild || !this.boundSnapshotReady) {
            this.maybeCaptureBoundSnapshot();
          } else {
            this.markBuildChanged();
          }
          this.validateForm();
        } catch (error) {
          console.error('Error in onVehicleSelected:', error);
        }
      }

      onGenerationSelected(event) {
        try {
          const { generation } = event.detail;
          this.selectedGeneration = generation;
          if (this.hydratingBuild || !this.boundSnapshotReady) {
            this.maybeCaptureBoundSnapshot();
          } else {
            this.markBuildChanged();
          }
          this.validateForm();
        } catch (error) {
          console.error('Error in onGenerationSelected:', error);
        }
      }

      onInstallLocationSelected(event) {
        try {
          this.selectedInstallLocation = event.detail?.value || '';
          if (this.hydratingBuild || !this.boundSnapshotReady) {
            this.maybeCaptureBoundSnapshot();
          } else {
            this.markBuildChanged();
          }
          if (this.selectedVehicle) this.validateForm();
        } catch (error) {
          console.error('Error in onInstallLocationSelected:', error);
        }
      }

      validateForm(options = {}) {
        try {
          const validation = this.getFormValidation();

          if (options.showErrors) {
            this.showValidationErrors(validation);
          } else {
            this.clearValidationErrors();
          }

          this.updateSubmitButton(validation.isValid);
          this.updateCartSubmitUI();

          return validation.isValid;
        } catch (error) {
          console.error('Error in validateForm:', error);
          return false;
        }
      }

      isViewCartAction() {
        return !!this.boundCartKey && !this.hasBuildChanges();
      }

      hasLoadedOrSavedBuild() {
        if (this.selectedVehicle?.value) return true;
        if (window.ConfiguratorUrlState && typeof window.ConfiguratorUrlState.hasCfgParams === 'function') {
          if (window.ConfiguratorUrlState.hasCfgParams()) return true;
        }
        if (new URLSearchParams(window.location.search).get('cfg_v')) return true;
        try {
          const session = window.ConfiguratorSession && typeof window.ConfiguratorSession.load === 'function'
            ? window.ConfiguratorSession.load()
            : null;
          if (session?.vehicle?.value) return true;
        } catch (error) {
          // Session restore is optional for Start over visibility.
        }
        return false;
      }

      shouldShowStartOver() {
        return !!this.boundCartKey || this.hasLoadedOrSavedBuild();
      }

      onConfiguratorReady() {
        this.syncSelectedStateFromDom();
        this.updateCartSubmitUI();
      }

      updateSubmitButton(isValid) {
        if (!this.submitButton) return;
        const canActivate = this.isViewCartAction() || isValid;
        this.submitButton.setAttribute('aria-disabled', String(!canActivate));
      }

      openCartDrawer() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open(this.submitButton);
          return;
        }
        if (this.cart && typeof this.cart.open === 'function') {
          this.cart.open(this.submitButton);
          return;
        }
        if (window.routes?.cart_url) {
          window.location = window.routes.cart_url;
        }
      }

      consumeStartOverUnbind() {
        try {
          if (sessionStorage.getItem('sp_cfg_unbound') === '1') {
            sessionStorage.removeItem('sp_cfg_unbound');
            return true;
          }
        } catch (error) {
          return false;
        }
        return false;
      }

      parseCartBuildKeys() {
        const raw = (this.dataset.cartBuildKeys || '').trim();
        if (!raw) return [];
        return raw.split(',').map((key) => key.trim()).filter(Boolean);
      }

      isCamperBuildCartItem(item) {
        if (!item) return false;
        if (this.productId && Number(item.product_id) !== Number(this.productId)) return false;
        if (this.cartBuildKeys.includes(item.key)) return true;
        const props = item.properties || {};
        return !!(props['Selected Vehicle'] || props['Camper SKU'] || props._cfg);
      }

      decodeCartKey(value) {
        if (!value) return null;
        try {
          return decodeURIComponent(value.replace(/\+/g, ' '));
        } catch (error) {
          return value;
        }
      }

      getUrlCartKey() {
        const live = new URLSearchParams(window.location.search).get('cfg_ck');
        if (live) return this.decodeCartKey(live);

        const bootSearch = window.__spConfiguratorBootSearch || '';
        if (bootSearch) {
          const bootKey = new URLSearchParams(bootSearch).get('cfg_ck');
          if (bootKey) return this.decodeCartKey(bootKey);
        }

        // Legacy cart links encoded the whole "cfg_ck=<key>" segment
        for (const source of [window.location.search, bootSearch]) {
          if (!source) continue;
          const legacyMatch = source.match(/(?:^|[?&])cfg_ck(?:=|%3D)([^&]+)/i);
          if (legacyMatch) return this.decodeCartKey(legacyMatch[1]);
        }

        return null;
      }

      resolveBoundCartKey(cartItems) {
        const hasLiveCart = arguments.length > 0 && Array.isArray(cartItems);
        const urlKey = this.getUrlCartKey();
        const buildItems = (cartItems || []).filter((item) => this.isCamperBuildCartItem(item));

        if (urlKey && buildItems.some((item) => item.key === urlKey)) return urlKey;
        if (buildItems.length === 1) return buildItems[0].key;
        if (buildItems.length > 1) return buildItems[buildItems.length - 1].key;

        if (!hasLiveCart) {
          if (urlKey && this.cartBuildKeys.includes(urlKey)) return urlKey;
          if (this.cartBuildKeys.length === 1) return this.cartBuildKeys[0];
          if (this.cartBuildKeys.length > 1) return this.cartBuildKeys[this.cartBuildKeys.length - 1];
        }

        return null;
      }

      applyLiveCartBinding(cart, preferredKey) {
        if (this.skipCartBinding && !preferredKey) {
          this.boundCartKey = null;
          this.cartBuildKeys = [];
          this.updateCartSubmitUI();
          return;
        }
        this.skipCartBinding = false;
        const items = cart?.items || [];
        const buildItems = items.filter((item) => this.isCamperBuildCartItem(item));
        this.cartBuildKeys = buildItems.map((item) => item.key);
        if (preferredKey && buildItems.some((item) => item.key === preferredKey)) {
          this.boundCartKey = preferredKey;
        } else {
          this.boundCartKey = this.resolveBoundCartKey(items);
        }
        this.userChangedBuild = false;
        this.hydratingBuild = true;
        this.boundSnapshotReady = false;
        this.bindBuildInteractionListeners();
        this.maybeCaptureBoundSnapshot();
        this.updateCartSubmitUI();
      }

      onCartUpdated(event) {
        if (event?.source === 'product-form') return;
        const cartData = event?.cartData;
        if (cartData && Array.isArray(cartData.items)) {
          this.applyLiveCartBinding(cartData);
          return;
        }
        this.refreshCartLineBinding();
      }

      async initCartLineBinding() {
        if (this.skipCartBinding) {
          this.boundCartKey = null;
          this.cartBuildKeys = [];
          this.updateCartSubmitUI();
          return;
        }

        const previousKey = this.boundCartKey;

        try {
          const response = await fetch('/cart.js');
          if (response.ok) {
            this.applyLiveCartBinding(await response.json());
            return;
          }
        } catch (error) {
          // Keep the page-rendered binding if the live cart cannot be read.
        }

        if (!this.boundCartKey) {
          this.boundCartKey = previousKey || this.resolveBoundCartKey();
        }
        this.userChangedBuild = false;
        this.hydratingBuild = true;
        this.boundSnapshotReady = false;
        this.maybeCaptureBoundSnapshot();
        this.updateCartSubmitUI();
      }

      async refreshCartLineBinding(preferredKey) {
        try {
          const response = await fetch('/cart.js');
          if (response.ok) {
            this.applyLiveCartBinding(await response.json(), preferredKey);
            return;
          }
        } catch (error) {
          // Keep the last known key if the live cart cannot be read.
        }

        if (preferredKey) this.boundCartKey = preferredKey;
        this.userChangedBuild = false;
        this.hydratingBuild = true;
        this.boundSnapshotReady = false;
        this.bindBuildInteractionListeners();
        this.maybeCaptureBoundSnapshot();
        this.updateCartSubmitUI();
      }

      updateCartSubmitUI() {
        if (!this.submitButton || !this.submitLabel) return;

        // Sold-out / unavailable buttons keep their own label from global.js
        if (this.submitButton.disabled) return;

        const addLabel = this.submitButton.dataset.addLabel || 'Add to cart';
        const updateLabel = this.submitButton.dataset.updateLabel || 'Update cart';
        const viewLabel = this.submitButton.dataset.viewLabel || 'View cart';
        const nextLabel = this.boundCartKey
          ? this.hasBuildChanges()
            ? updateLabel
            : viewLabel
          : addLabel;
        if (this.submitLabel.textContent !== nextLabel) {
          this.submitLabel.textContent = nextLabel;
        }
        this.submitButton.setAttribute('aria-disabled', String(!(this.isViewCartAction() || this.getFormValidation().isValid)));

        if (this.secondaryActions) {
          const showSecondary = this.shouldShowStartOver();
          this.secondaryActions.classList.toggle('hidden', !showSecondary);
          this.secondaryActions.toggleAttribute('hidden', !showSecondary);
        }
      }

      onStartOverClick(event) {
        event.preventDefault();
        if (window.ConfiguratorProfileBuilds && typeof window.ConfiguratorProfileBuilds.startOver === 'function') {
          window.ConfiguratorProfileBuilds.startOver(event.currentTarget);
          return;
        }
        if (window.ConfiguratorCart && typeof window.ConfiguratorCart.startOver === 'function') {
          window.ConfiguratorCart.startOver();
        }
      }

      buildFormData() {
        const formData = new FormData(this.form);
        const options = [];

        for (const [key, value] of formData.entries()) {
          if (key === 'properties[Options]') {
            options.push(value);
          }
        }

        for (const [key] of formData.entries()) {
          if (key === 'properties[Options]') {
            formData.delete(key);
          }
        }

        if (this.selectedVehicle?.value) {
          formData.append('properties[Selected Vehicle]', this.getSelectedVehicleLabel());
        }

        if (this.selectedGeneration) {
          formData.append('properties[Generation]', this.selectedGeneration);
        }

        if (options.length > 0) {
          formData.append('properties[Options]', options.join(', '));
        }

        const requiredAddons = this.calculateRequiredAddons();
        if (requiredAddons !== null) {
          formData.append('properties[Required Addons]', requiredAddons);
        }

        const installFee = this.calculateInstallFee();
        if (installFee !== null) {
          formData.append('properties[Install Fee]', installFee);
        }

        const locationCharges = this.calculateLocationCharges(installFee);
        if (locationCharges) {
          formData.append('properties[Shipping]', locationCharges.shippingProperty);
        }

        if (this.isInstallLocationRequired()) {
          const propertyName = this.installLocationSelector?.dataset.propertyName || 'Install Location';
          const installLocationKey = `properties[${propertyName}]`;
          const installLocation = this.getInstallLocationValue();
          formData.delete(installLocationKey);
          if (installLocation) {
            formData.append(installLocationKey, installLocation);
          }
        }

        const customSku = this.buildcustomSkuString(formData);
        if (customSku) {
          formData.append('properties[Camper SKU]', customSku);
        }

        if (this.cart?.getSectionsToRender) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        return formData;
      }

      extractAddedItemKey(response) {
        if (!response) return null;
        if (typeof response.key === 'string' && response.key) {
          return response.key;
        }
        if (!response?.items?.length) return null;
        const addedVariantId = String(response.items[response.items.length - 1].variant_id || response.items[response.items.length - 1].id || '');
        const matchingItems = response.items.filter((item) => String(item.variant_id || item.id) === addedVariantId);
        return matchingItems.length ? matchingItems[matchingItems.length - 1].key : response.items[response.items.length - 1].key;
      }

      getLinePropertiesFromFormData(formData) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = value;
        }
        return properties;
      }

      async changeCartLine(lineKey, quantity, properties) {
        const payload = {
          id: lineKey,
          quantity,
          properties,
        };

        if (this.cart?.getSectionsToRender) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: JSON.stringify(payload),
        });

        return response.json();
      }

      async removeCartLine(lineKey) {
        const payload = {
          id: lineKey,
          quantity: 0,
        };

        if (this.cart?.getSectionsToRender) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: JSON.stringify(payload),
        });

        const parsedState = await response.json();
        if (parsedState.errors || parsedState.description) {
          throw new Error(parsedState.description || parsedState.errors || 'Could not remove the previous cart item.');
        }

        return parsedState;
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        const validation = this.getFormValidation();
        if (!validation.isValid) {
          this.showValidationErrors(validation);
          return;
        }

        if (this.isViewCartAction()) {
          this.openCartDrawer();
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = this.buildFormData();
        const isUpdate = !!this.boundCartKey;
        const previousKey = this.boundCartKey;
        const formQty = Number(formData.get('quantity') || 1);

        config.body = formData;

        let before = { cartCount: null, lineQty: null, lineVariant: null };
        try {
          const cart = await (await fetch('/cart.js')).json();
          const line = (cart.items || []).find((item) => item.key === previousKey);
          before = { cartCount: cart.item_count, lineQty: line?.quantity ?? null, lineVariant: line?.variant_id ?? null };
        } catch (error) {
          before = { cartCount: null, lineQty: null, lineVariant: null, error: true };
        }

        const nextVariantId = String(formData.get('id') || '');
        const sameVariant = !!(
          isUpdate &&
          previousKey &&
          before.lineVariant &&
          String(before.lineVariant) === nextVariantId
        );

        try {
          if (sameVariant) {
            const properties = this.getLinePropertiesFromFormData(formData);
            const changeResponse = await this.changeCartLine(previousKey, formQty || 1, properties);
            if (changeResponse.status || changeResponse.errors || changeResponse.description) {
              this.handleErrorResponse(changeResponse, formData);
              return;
            }

            const updatedLine =
              (changeResponse.items || []).find((item) => item.key === previousKey) ||
              (changeResponse.items || []).find(
                (item) => this.isCamperBuildCartItem(item) && String(item.variant_id) === nextVariantId
              );

            await this.refreshCartLineBinding(updatedLine?.key || previousKey);
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
            this.handleSuccessResponse(changeResponse, formData);
            return;
          }

          const response = await (await fetch(`${routes.cart_add_url}`, config)).json();
          if (response.status) {
            this.handleErrorResponse(response, formData);
            return;
          }

          const newKey = this.extractAddedItemKey(response);

          if (isUpdate && previousKey && newKey && newKey !== previousKey) {
            try {
              const changeResponse = await this.removeCartLine(previousKey);
              await this.refreshCartLineBinding(newKey);
              if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }
              const renderResponse = changeResponse.sections ? changeResponse : response;
              this.handleSuccessResponse(renderResponse, formData);
              return;
            } catch (error) {
              console.error('Cart update remove failed:', error);
              if (newKey) {
                this.boundCartKey = newKey;
                this.updateCartSubmitUI();
              }
              this.handleErrorMessage(
                'Your updated build was added, but the previous cart item could not be removed. Please check your cart.'
              );
              if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }
              this.handleSuccessResponse(response, formData);
              return;
            }
          }

          if (newKey) {
            this.skipCartBinding = false;
            this.boundCartKey = newKey;
            this.cartBuildKeys = [...new Set([...(this.cartBuildKeys || []), newKey])];
            this.updateCartSubmitUI();
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.handleSuccessResponse(response, formData);
        } catch (error) {
          console.error('Form submission error:', error);
          if (String(error.message || '').includes("Cannot read properties of undefined (reading 'cart-drawer')")) {
            window.location = window.routes.cart_url;
          }
        } finally {
          this.resetFormState();
        }
      }

      getSelectedVehicleLabel() {
        const selectedOption = document.querySelector('#vehicle-model-list .disclosure__link[aria-selected="true"]');
        const optionLabel = selectedOption?.dataset?.optionText?.trim();
        if (optionLabel) return optionLabel;

        const trigger = document.querySelector('#vehicle-model-value');
        const triggerLabel = trigger?.dataset?.optionText?.trim() || trigger?.textContent?.trim();
        const placeholder = trigger?.dataset.label;
        if (triggerLabel && triggerLabel !== placeholder) return triggerLabel;

        if (this.selectedVehicle?.label) return this.selectedVehicle.label;
        return this.formatVehicleName(this.selectedVehicle.value);
      }

      formatVehicleName(vehicleName) {
        return (
          vehicleName
            // Split on hyphens
            .split('-')
            // Capitalize first letter of each word
            .map((word) => {
              // Handle numbers specially
              if (/^\d+$/.test(word)) {
                return word;
              }
              // Handle special cases for proper capitalization
              const specialCases = {
                chevy: 'Chevy',
                gmc: 'GMC',
                ford: 'Ford',
                ram: 'RAM',
                bed: 'Bed',
              };

              return specialCases[word.toLowerCase()] || word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            })
            // Join with spaces
            .join(' ')
        );
      }

      // Build Camper SKU string from form selections
      buildcustomSkuString(formData) {
        try {
          const skuComponents = [];

          // 1. Product code from custom product SKU data attribute
          let productSku = '';

          // Try to get product code from the product-info element's data attribute
          const productInfoElement = this.closest('product-info');
          if (productInfoElement && productInfoElement.dataset.customProductSku) {
            productSku = productInfoElement.dataset.customProductSku;
          }

          if (!productSku) {
            throw new Error('Product SKU not found - check data-custom-product-sku attribute');
          }

          skuComponents.push(productSku);

          // 2. Vehicle model Camper SKU + Generation
          let vehicleModelFitment = '';
          let generation = '';

          // Get vehicle Camper SKU from selected vehicle element
          const selectedVehicle = document.querySelector('.disclosure__link[aria-selected="true"]');
          if (selectedVehicle) {
            vehicleModelFitment = selectedVehicle.getAttribute('data-vehicle-odoo-sku') || '';
          }

          if (!vehicleModelFitment) {
            throw new Error('Vehicle model/fitment not selected');
          }

          // Get generation from selected generation or form data
          // if (this.selectedGeneration && this.selectedGeneration !== 'Select Generation') {
          //   generation = this.parseGenerationToCode(this.selectedGeneration);
          // } else {
          //   // Try to get from form data
          //   for (const [key, value] of formData.entries()) {
          //     if (key === 'properties[Generation]') {
          //       generation = this.parseGenerationToCode(value) || '';
          //       break;
          //     }
          //   }
          // }

          // Combine vehicle sku with generation
          const vehicleComponent = vehicleModelFitment + generation;
          skuComponents.push(vehicleComponent);

          // 3. Parse variant selections for bed length, windows, doors, cabside
          let bedLength = '';
          let rearDoor = '';
          // let driverSide = ''; -- save for later
          // let passengerSide = ''; -- save for later
          let cabside = '';
          let canopyColor = '';
          let tentColor = '';

          // Check for TRUMA addon to set passenger side to TRU
          let hasTruma = false;
          for (const [key, value] of formData.entries()) {
            if (key.includes('Add Ons') && value.toLowerCase().includes('truma')) {
              hasTruma = true;
              break;
            }
          }

          // Get variant ID from URL query parameter
          const urlParams = new URLSearchParams(window.location.search);
          const variantId = urlParams.get('variant');

          if (variantId) {
            // Get the actual selected color inputs
            const selectedCanopyInput = document.querySelector('input[name="Canopy Color"]:checked');
            const selectedTentInput = document.querySelector('input[name="Tent Color"]:checked');

            if (selectedCanopyInput && selectedTentInput) {
              const canopyColorName = selectedCanopyInput.value;
              const tentColorName = selectedTentInput.value;

              // Get the correct variant data from Shopify Analytics
              const allScripts = document.querySelectorAll('script');
              let productData = null;

              allScripts.forEach((script) => {
                if (
                  script.textContent &&
                  script.textContent.includes('ShopifyAnalytics') &&
                  script.textContent.includes('product')
                ) {
                  try {
                    const scriptContent = script.textContent;
                    const metaMatch = scriptContent.match(/var meta = ({.*?});/s);
                    if (metaMatch) {
                      const metaData = JSON.parse(metaMatch[1]);
                      if (metaData.product) {
                        productData = metaData.product;
                      }
                    }
                  } catch (e) {
                    // Error parsing script, continue
                  }
                }
              });

              if (productData && productData.variants) {
                // Find the variant that matches our ID
                const variant = productData.variants.find((v) => v.id.toString() === variantId);
                if (variant) {
                  // Parse the variant name to extract colors
                  const nameToParse = variant.public_title || variant.name;

                  if (nameToParse) {
                    const parts = nameToParse.split('/').map((part) => part.trim());

                    if (parts.length >= 3) {
                      const size = parts[0];
                      const variantCanopyColor = parts[1];
                      // Handle tent color that might be split across multiple parts
                      let variantTentColor = parts[2];
                      if (parts.length > 3) {
                        // Combine the tent color parts (e.g., "Navy" + "Oyster" = "Navy / Oyster")
                        variantTentColor = parts.slice(2).join(' / ');
                      }

                      // Check if the variant colors match our selected colors
                      if (variantCanopyColor === canopyColorName && variantTentColor === tentColorName) {
                        // Extract colors from the variant's SKU (e.g., "X1-DEP-FULL-SND-NVY")
                        const skuParts = variant.sku.split('-');
                        if (skuParts.length >= 2) {
                          // The last two parts should be the colors (SND-NVY)
                          canopyColor = skuParts[skuParts.length - 2]; // SND
                          tentColor = skuParts[skuParts.length - 1]; // NVY
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          // Get variant odoo strings from selected options - ALL options with data-variant-odoo-sku
          const selectedVariantOptions = document.querySelectorAll('input[data-variant-odoo-sku]:checked');
          const selectedOdooOptions = document.querySelectorAll('input[data-odoo-sku]:checked');

          // Process variant options (data-variant-odoo-sku)
          selectedVariantOptions.forEach((option, index) => {
            const customSku = option.getAttribute('data-variant-odoo-sku');
            const optionName = option.name;
            const optionValue = option.value;

            if (customSku) {
              const optionValueLower = optionValue.toLowerCase();
              const optionNameLower = optionName.toLowerCase();

              // Check for bed length - look in both name and value, and also check the selected vehicle title
              if (
                optionNameLower.includes('bed') ||
                optionValueLower.includes('bed') ||
                optionNameLower.includes('size') ||
                optionValueLower.includes('size')
              ) {
                // Gather vehicle label from multiple sources (option text can differ from trigger/handle)
                const selectedOptionEl = document.querySelector(
                  '#vehicle-model-list .disclosure__link[aria-selected="true"]'
                );
                const triggerEl = document.querySelector('#vehicle-model-value');
                const vehicleLabelCandidates = [
                  this.getSelectedVehicleLabel(),
                  selectedOptionEl?.dataset?.optionText,
                  triggerEl?.dataset?.optionText,
                  selectedOptionEl?.getAttribute?.('data-value'),
                  this.selectedVehicle?.label,
                  this.selectedVehicle?.value,
                ]
                  .map((v) => String(v || '').trim())
                  .filter(Boolean);

                const selectedVehicleTitle = (vehicleLabelCandidates[0] || '').toLowerCase();
                const vehicleSearchText = vehicleLabelCandidates.join(' | ').toLowerCase();

                // Truck beds are 4'–9' (Rivian R1T is 4' 6"). Reject model numbers (150/1500/2500) and years.
                const MIN_BED_FEET = 4;
                const MAX_BED_FEET = 9;
                const MIN_BED_INCHES = 48; // 4'0"
                const MAX_BED_INCHES = 108; // 9'0"
                const BED_NEAR_CHARS = 20;
                // Apostrophe / prime marks seen in CMS + HTML entities
                const FT = `['’′ʼ\`´]`;
                const IN = `["”″〞]`;

                const isValidBedMeasurement = (feet, inches) => {
                  if (!Number.isFinite(feet) || !Number.isFinite(inches)) return false;
                  if (feet < MIN_BED_FEET || feet > MAX_BED_FEET) return false;
                  if (inches < 0 || inches > 11) return false;
                  const total = feet * 12 + inches;
                  return total >= MIN_BED_INCHES && total <= MAX_BED_INCHES;
                };

                const distanceToBedWord = (text, matchIndex, matchLength) => {
                  const bedRegex = /\bbed\b/gi;
                  let best = Infinity;
                  let bedMatch;
                  while ((bedMatch = bedRegex.exec(text)) !== null) {
                    const bedStart = bedMatch.index;
                    const bedEnd = bedStart + bedMatch[0].length;
                    const matchEnd = matchIndex + matchLength;
                    const dist =
                      matchEnd < bedStart
                        ? bedStart - matchEnd
                        : bedEnd < matchIndex
                          ? matchIndex - bedEnd
                          : 0;
                    if (dist < best) best = dist;
                  }
                  return best;
                };

                // Labels are "Make Model | 8' | Years": a measurement alone in its segment is the bed length
                const isOwnLabelSegment = (text, matchIndex, matchLength) =>
                  /(?:^|\|)\s*$/.test(text.slice(0, matchIndex)) &&
                  /^\s*(?:\||$)/.test(text.slice(matchIndex + matchLength));

                // True when label/handle clearly encodes a sub-6' bed (blocks bad Size→LNG fallback)
                const hasShortBedHint = (text) => {
                  if (!text) return false;
                  if (/\bshort\b/i.test(text)) return true;
                  // 5'…, 5-7 bed, 5 7 bed, …-5-7-bed-… (handle form)
                  return (
                    new RegExp(`\\b[45]\\s*${FT}`, 'i').test(text) ||
                    /\b[45]\s*[-–]\s*[0-9]{1,2}\b/.test(text) ||
                    /\b[45]\s+[0-9]{1,2}\s+bed\b/i.test(text) ||
                    /-[45]-\d{1,2}-bed\b/i.test(text) ||
                    /\b[45]-\d{1,2}-bed\b/i.test(text)
                  );
                };

                // Parse numeric bed length from the vehicle label only (not Size option text).
                const parseBedLengthToInches = (text) => {
                  if (!text) return null;

                  const patterns = [
                    // "5' 7"" / "5'7""
                    new RegExp(`(\\d+)\\s*${FT}\\s*(\\d+)\\s*${IN}`, 'g'),
                    // "5' 7" without inch mark
                    new RegExp(`(\\d+)\\s*${FT}\\s*(\\d+)`, 'g'),
                    // "5-7 bed" / "5-7-bed" (hyphenated CMS / URL handles)
                    /(\d+)\s*[-–]\s*(\d+)\s*(?:["”″〞])?\s*[-–\s]*bed\b/gi,
                    // "5 7 bed" (handle expanded via formatVehicleName: "1500 5 7 Bed")
                    /(\d+)\s+(\d+)\s+bed\b/gi,
                    // "5 feet 7 inches"
                    /(\d+)\s*(?:foot|feet)\s*(\d+)\s*inches?/gi,
                    // "5' bed" / "5.5' bed"
                    new RegExp(`(\\d+\\.?\\d*)\\s*${FT}\\s*(?=bed)`, 'gi'),
                    // "5'" near the word bed
                    new RegExp(`(\\d+\\.?\\d*)\\s*${FT}`, 'g'),
                    // "8 foot" / "8 feet" (truck bed sizing language)
                    /(\d+\.?\d*)\s+(?:foot|feet)\b/gi,
                  ];

                  const candidates = [];

                  patterns.forEach((pattern, patternIndex) => {
                    pattern.lastIndex = 0;
                    let match;
                    while ((match = pattern.exec(text)) !== null) {
                      const feet = parseFloat(match[1]);
                      const inches = match[2] != null && match[2] !== '' ? parseFloat(match[2]) : 0;
                      if (!isValidBedMeasurement(feet, inches)) continue;

                      const dist = distanceToBedWord(text, match.index, match[0].length);
                      // Bare "5'" (no inch component) must sit near the word "bed" or be its own label segment
                      const isBareApostrophePattern = patternIndex === 6;
                      if (
                        isBareApostrophePattern &&
                        dist > BED_NEAR_CHARS &&
                        !isOwnLabelSegment(text, match.index, match[0].length)
                      ) {
                        continue;
                      }

                      candidates.push({
                        totalInches: feet * 12 + inches,
                        dist: Number.isFinite(dist) ? dist : 1000,
                        patternIndex,
                        index: match.index,
                      });
                    }
                  });

                  if (candidates.length === 0) return null;

                  candidates.sort((a, b) => {
                    if (a.dist !== b.dist) return a.dist - b.dist;
                    if (a.patternIndex !== b.patternIndex) return a.patternIndex - b.patternIndex;
                    return a.index - b.index;
                  });

                  return candidates[0].totalInches;
                };

                // No trailing \b after the apostrophe: "8' |" has no word boundary there
                const longSizePattern = /\b8(?:\s*['’′]|[\s-]*(?:foot|feet)\b)/i;
                const longBedPattern = /\blong\s*(?:bed|size)?\b/i;

                // Numeric parse from vehicle label/handle sources first
                let bedLengthInches = null;
                for (const label of vehicleLabelCandidates) {
                  bedLengthInches = parseBedLengthToInches(label.toLowerCase());
                  if (bedLengthInches !== null) break;
                }

                // Keyword / Size fallbacks only when no valid 5'–9' measurement was found.
                // Never let Size "8 Foot Truck" win over a short-bed hint in the vehicle string.
                if (bedLengthInches === null) {
                  const shortHint = hasShortBedHint(vehicleSearchText);
                  const isExplicitLongSize =
                    !shortHint &&
                    (longSizePattern.test(selectedVehicleTitle) ||
                      longBedPattern.test(selectedVehicleTitle) ||
                      longSizePattern.test(optionValueLower) ||
                      longBedPattern.test(optionValueLower));

                  if (shortHint || selectedVehicleTitle.includes('short') || optionValueLower.includes('short')) {
                    bedLengthInches = 71;
                  } else if (isExplicitLongSize) {
                    bedLengthInches = 96;
                  }
                }

                if (bedLengthInches !== null) {
                  // < 6' (72") = SHRT, 6'–<8' = REG, >= 8' (96") = LNG
                  let classification = 'REG';
                  if (bedLengthInches < 72) {
                    classification = 'SHRT';
                  } else if (bedLengthInches >= 96) {
                    classification = 'LNG';
                  }
                  bedLength = classification;
                } else {
                  bedLength = 'REG';
                }
              } else if (optionNameLower.includes('canopy') || optionValueLower.includes('canopy')) {
                // Only extract canopy color if we don't already have it from variant SKU
                if (!canopyColor) {
                  // Extract canopy color from the variant's Camper SKU (e.g., "BLU-GRY" -> "BLU")
                  const colorParts = customSku.split('-');
                  if (colorParts.length >= 1) {
                    canopyColor = colorParts[0]; // First part is canopy color
                  }
                }
              } else if (optionNameLower.includes('tent') || optionValueLower.includes('tent')) {
                // Only extract tent color if we don't already have it from variant SKU
                if (!tentColor) {
                  // Extract tent color from the variant's Camper SKU (e.g., "BLK-NVY" -> "NVY")
                  const colorParts = customSku.split('-');
                  if (colorParts.length >= 2) {
                    tentColor = colorParts[1]; // Second part is tent color
                  }
                }
              }
            }
          });

          // Process odoo options (data-odoo-sku) - Product Options like Rear Door, etc.
          selectedOdooOptions.forEach((option, index) => {
            const customSku = option.getAttribute('data-odoo-sku');
            const optionName = option.name;
            const optionValue = option.value;

            if (customSku) {
              const optionValueLower = optionValue.toLowerCase();
              const optionNameLower = optionName.toLowerCase();

              if (optionNameLower.includes('rear') || optionValueLower.includes('rear')) {
                // Parse the Camper SKU which appears to be "WND :: SLD"
                const odooParts = customSku.split(' :: ');
                if (odooParts.length >= 2) {
                  rearDoor = optionValueLower.includes('solid') ? odooParts[1] : odooParts[0];
                } else {
                  rearDoor = optionValueLower.includes('solid') ? 'SLD' : 'WND';
                }
              } else if (optionNameLower.includes('cabside') || optionValueLower.includes('cabside')) {
                // For cabside, use the Camper SKU directly since it's already the correct code
                cabside = customSku;
              }
            }
          });

          // Fallback: if we still don't have colors, try to get them from the selected inputs
          if (!canopyColor || !tentColor) {
            const canopyColorInput = document.querySelector('input[name="Canopy Color"]:checked');
            if (canopyColorInput) {
              const canopyOdooSku = canopyColorInput.getAttribute('data-variant-odoo-sku');
              if (canopyOdooSku) {
                const canopyParts = canopyOdooSku.split('-');
                if (canopyParts.length >= 1) {
                  canopyColor = canopyParts[0];
                }
              }
            }

            const tentColorInput = document.querySelector('input[name="Tent Color"]:checked');
            if (tentColorInput) {
              const tentOdooSku = tentColorInput.getAttribute('data-variant-odoo-sku');
              if (tentOdooSku) {
                const tentParts = tentOdooSku.split('-');
                if (tentParts.length >= 2) {
                  tentColor = tentParts[1];
                }
              }
            }
          }

          // Override passenger side if TRUMA addon is selected
          // if (hasTruma) {
          //   passengerSide = 'TRU';
          // }

          // Validate all required components are present
          if (!bedLength) {
            throw new Error('Bed length not selected');
          }
          if (!rearDoor) {
            throw new Error('Rear door/window not selected');
          }
          // if (!driverSide) {
          //   throw new Error('Driver side door/window not selected');
          // }
          // if (!passengerSide) {
          //   throw new Error('Passenger side door/window not selected');
          // }
          if (!cabside) {
            // Default to STD if cabside is not selected
            cabside = 'STD';
          }
          if (!canopyColor) {
            throw new Error('Canopy color not selected');
          }
          if (!tentColor) {
            throw new Error('Tent color not selected');
          }

          // 4. Build final SKU components
          skuComponents.push(bedLength);
          skuComponents.push(rearDoor);
          // skuComponents.push(driverSide);
          // skuComponents.push(passengerSide);
          skuComponents.push(cabside);
          skuComponents.push(canopyColor);
          skuComponents.push(tentColor);

          // Join with hyphens
          const finalSku = skuComponents.join('-');

          return finalSku;
        } catch (error) {
          console.error('❌ Error building Camper SKU:', error);
          console.error('Error details:', error.message);
          return '';
        }
      }

      // Parse generation string to generation code (Gen 1 -> G1, Gen 2 -> G2, etc.)
      // parseGenerationToCode(generationString) {
      //   if (!generationString || generationString === 'Select Generation') {
      //     return '';
      //   }

      //   // Convert "Gen 1" to "G1", "Gen 2" to "G2", etc.
      //   const match = generationString.match(/Gen\s*(\d+)/i);
      //   if (match) {
      //     return `G${match[1]}`;
      //   }

      //   // Handle other generation formats
      //   if (generationString.toLowerCase().includes('generation')) {
      //     const numMatch = generationString.match(/(\d+)/);
      //     if (numMatch) {
      //       return `G${numMatch[1]}`;
      //     }
      //   }

      //   // If no number found, default to G0
      //   return '';
      // }

      // Other helper methods remain the same
      handleErrorResponse(response, formData) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          errors: response.errors || response.description,
          message: response.message,
        });

        this.handleErrorMessage(response.description);

        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (soldOutMessage) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.querySelector('span').classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
        }
      }

      handleSuccessResponse(response, formData) {
        try {
          if (!this.error) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  if (response.sections) {
                    this.cart.renderContents(response);
                  } else {
                    // Fallback if sections data is missing
                    window.location = window.routes.cart_url;
                  }
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            if (response.sections) {
              this.cart.renderContents(response);
            } else {
              // Fallback if sections data is missing
              window.location = window.routes.cart_url;
            }
          }
        } catch (error) {
          console.error('Error in handleSuccessResponse:', error);
          window.location = window.routes.cart_url;
        }
      }

      calculateInstallFee() {
        try {
          // Check if install fee is active (element exists)
          const installFeeElement = document.getElementById('install-fee');
          if (!installFeeElement) {
            return null; // No install fee if element doesn't exist
          }

          const addOnInputs = document.querySelectorAll('input[data-price]:checked');

          let addOnPrice = 0;
          addOnInputs.forEach((input) => {
            if (input.disabled) return;
            const wrapper = input.closest('.add-on-product-wrapper');
            if (wrapper) {
              const wrapperDisplay = wrapper.style.display || window.getComputedStyle(wrapper).display;
              if (wrapperDisplay === 'none') return;
            }
            const price = parseFloat(String(input.getAttribute('data-price') || '').replace(/[^0-9.]/g, '')) || 0;
            addOnPrice += price;
          });

          // Get install fee from the display element
          const installFeeText = installFeeElement.textContent;
          const installFeeMatch = installFeeText.match(/\$?([0-9,]+\.?[0-9]*)/);
          let installFee = 200; // Default fallback
          if (installFeeMatch) {
            installFee = parseFloat(installFeeMatch[1].replace(/,/g, ''));
          }

          // Install fee gets waived when customer spends $2000+ on add-ons
          const isInstallFeeWaived = addOnPrice >= 2000;
          const installFeeFormatted = `$${installFee.toFixed(2)}`;
          return isInstallFeeWaived ? '$0.00 (Waived)' : installFeeFormatted;
        } catch (error) {
          console.error('Error calculating install fee:', error);
          return null; // Return null if calculation fails
        }
      }

      calculateLocationCharges(installFeeValue) {
        if (!this.installLocationSelector || typeof this.installLocationSelector.getCharges !== 'function') {
          return null;
        }

        const displayPriceElement = document.getElementById('display-price');
        const addOnTotalElement = document.getElementById('add-on-total');
        const requiredAddOnsElement = document.getElementById('required-addons-total');
        const basePrice = displayPriceElement
          ? parseFloat(String(displayPriceElement.textContent || '').replace(/[^0-9.]/g, '')) || 0
          : 0;
        const optionsPrice = addOnTotalElement
          ? parseFloat(String(addOnTotalElement.textContent || '').replace(/[^0-9.]/g, '')) || 0
          : 0;
        const requiredAddOnPrice = requiredAddOnsElement
          ? parseFloat(String(requiredAddOnsElement.textContent || '').replace(/[^0-9.]/g, '')) || 0
          : 0;
        let installDollars = 0;
        if (installFeeValue && !/waived/i.test(String(installFeeValue))) {
          installDollars = parseFloat(String(installFeeValue).replace(/[^0-9.]/g, '')) || 0;
        }

        const taxableCents = Math.round((basePrice + optionsPrice + requiredAddOnPrice + installDollars) * 100);
        return this.installLocationSelector.getCharges(taxableCents);
      }

      calculateRequiredAddons() {
        try {
          const requiredAddOnsElement = document.getElementById('required-addons-total');
          let requiredAddOnPrice = 0;

          if (requiredAddOnsElement) {
            requiredAddOnPrice =
              parseFloat(String(requiredAddOnsElement.textContent || '').replace(/[^0-9.]/g, '')) || 0;
          } else {
            document.querySelectorAll('#add-on-products input[data-price]:checked').forEach((input) => {
              if (input.disabled) return;
              const wrapper = input.closest('.add-on-product-wrapper');
              if (wrapper) {
                const wrapperDisplay = wrapper.style.display || window.getComputedStyle(wrapper).display;
                if (wrapperDisplay === 'none') return;
              }
              requiredAddOnPrice +=
                parseFloat(String(input.getAttribute('data-price') || '').replace(/[^0-9.]/g, '')) || 0;
            });
          }

          if (requiredAddOnPrice <= 0) return null;
          return `$${requiredAddOnPrice.toFixed(2)}`;
        } catch (error) {
          console.error('Error calculating required addons:', error);
          return null;
        }
      }

      resetFormState() {
        this.submitButton.classList.remove('loading');
        if (this.cart?.classList.contains('is-empty')) {
          this.cart.classList.remove('is-empty');
        }
        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
        }
        this.querySelector('.loading__spinner').classList.add('hidden');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        if (this.errorMessageWrapper) {
          this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        }

        if (this.errorMessage && errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }
    }
  );
}
